import styled from 'styled-components';
import { fontSizeDefinitions_DEPRECATED } from '../../atomicComponents/fonts/fontSizeDefinitionsWithDefaultFont';
import type { ArdoqId, PersistedExternalDocument } from '@ardoq/api-types';
import type { RepresentationData } from '@ardoq/data-model';
import { t, type I18nLocale } from '@ardoq/i18n';
import { MouseEvent } from 'react';
import { CUSTOM_SVG_ICON_SELECTOR, Icon, IconName } from '@ardoq/icons';
import {
  CONTENT_MAX_WIDTH,
  ICON_SIZE,
  horizontalSpacing,
} from 'tabview/pagesView/constants';
import { ComponentPath } from 'tabview/pagesView/ComponentPath';
import type { ComponentPathData } from 'tabview/pagesView/types';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { colors, s16 } from '@ardoq/design-tokens';
import { FlexBox } from '@ardoq/layout';
import { ExternalDocumentActions } from './ExternalDocumentsActions';

const HeaderLayout = styled.div`
  display: flex;
  align-items: center;
  padding-left: ${horizontalSpacing.SMALL};
  user-select: none;
  gap: ${s16};
`;
const ComponentName = styled.span`
  ${fontSizeDefinitions_DEPRECATED.LARGE}
  color: ${colors.grey950};
  font-weight: bold;
  &:hover {
    cursor: pointer;
    text-decoration: underline;
  }
`;
const RepresentationContainer = styled.div`
  display: flex;
  margin-right: ${horizontalSpacing.MEDIUM};
  min-width: ${ICON_SIZE};
  text-align: center;

  && > ${CUSTOM_SVG_ICON_SELECTOR} {
    min-width: ${ICON_SIZE};
    max-width: ${ICON_SIZE};
  }
`;

const ComponentButton = styled(Icon)`
  color: ${colors.grey950};
  cursor: pointer;
`;

const LockIcon = styled(Icon)`
  margin-left: ${horizontalSpacing.SMALL};
`;

const HeaderContainer = styled.div`
  break-inside: avoid;
  break-before: auto;
  max-width: ${CONTENT_MAX_WIDTH};
`;

const ComponentActionsContainer = styled(FlexBox).attrs({
  flex: 1,
  align: 'center',
  justify: 'flex-end',
  gap: 'medium',
})``;

type ComponentHeaderProperties = {
  componentId: ArdoqId;
  isLocked: boolean;
  representationData: RepresentationData;
  displayLabel: string;
  fillColor: string;
  externalDocuments: PersistedExternalDocument[];
  componentPath: ComponentPathData | undefined;
  hasWriteAccess: boolean;
  showContextMenu: boolean;
  i18nLocale: I18nLocale;
  onContextMenuClick: (event: MouseEvent) => void;
};
const ComponentHeader = ({
  componentId,
  isLocked,
  representationData,
  displayLabel,
  fillColor,
  externalDocuments,
  componentPath,
  hasWriteAccess,
  showContextMenu,
  i18nLocale,
  onContextMenuClick,
}: ComponentHeaderProperties) => (
  <HeaderContainer>
    <HeaderLayout data-click-namespace="pages view component header">
      <RepresentationContainer>
        <ComponentRepresentation
          {...representationData}
          style={{ fontSize: 24, color: fillColor }}
        />
      </RepresentationContainer>
      <ComponentName
        className="component"
        data-global-handler-id={componentId}
        role="link"
      >
        {displayLabel}
      </ComponentName>
      {isLocked && <LockIcon iconName={IconName.LOCK} />}
      <ComponentActionsContainer>
        {externalDocuments.length > 0 && (
          <ExternalDocumentActions
            componentId={componentId}
            externalDocuments={externalDocuments}
            hasWriteAccess={hasWriteAccess}
            i18nLocale={i18nLocale}
          />
        )}
        {showContextMenu && (
          <ComponentButton
            data-tooltip-text={t(
              i18nLocale,
              'tabview:pagesView.componentHeader.showContextMenu',
              'Show component context menu'
            )}
            data-click-id="edit-component-button"
            deprecated_onClick={onContextMenuClick}
            iconName={IconName.MORE_VERT}
          />
        )}
      </ComponentActionsContainer>
    </HeaderLayout>
    {componentPath && <ComponentPath {...componentPath} small={true} />}
  </HeaderContainer>
);

export { ComponentHeader };
